import type { Question, Validation } from '../types';

export class QuestionRenderer {
  static renderQuestion(question: Question, currentAnswer: string | string[]): string {
    const placeholder = question.placeholder || '';

    switch (question.type) {
      case 'text':
        return this.renderTextInput(currentAnswer as string, placeholder);
      case 'email':
        return this.renderEmailInput(currentAnswer as string, placeholder);
      case 'number':
        return this.renderNumberInput(currentAnswer as string, placeholder, question.validation);
      case 'date':
        return this.renderDateInput(currentAnswer as string);
      case 'time':
        return this.renderTimeInput(currentAnswer as string);
      case 'textarea':
        return this.renderTextarea(currentAnswer as string, placeholder);
      case 'dropdown':
        return this.renderSelect(currentAnswer as string, question.options!);
      case 'select':
        return this.renderRadioButtons(currentAnswer as string, question.options!);
      case 'radio':
        return this.renderRadioButtons(currentAnswer as string, question.options!);
      case 'checkbox':
        return this.renderCheckboxes(currentAnswer, question.options!);
      case 'rating':
        return this.renderRating(currentAnswer as string, question.max || 5, question.minLabel, question.maxLabel);
      case 'grid':
        return this.renderGrid(currentAnswer, question.rows!, question.columns!);
      case 'static':
        return this.renderStaticText(question.text || question.question, question.description);
 
      default:
        return '';
    }
  }

  private static renderTextInput(value: string, placeholder: string): string {
    return `<input type="text" id="answer" value="${value}" placeholder="${placeholder}">`;
  }

  private static renderEmailInput(value: string, placeholder: string): string {
    return `<input type="email" id="answer" value="${value}" placeholder="${placeholder}">`;
  }

  private static renderNumberInput(value: string, placeholder: string, validation?: Validation): string {
    const min = validation?.rules?.min !== undefined ? `min="${validation.rules.min}"` : '';
    const max = validation?.rules?.max !== undefined ? `max="${validation.rules.max}"` : '';
    const step = 'step="any"'; // Allow both integers and decimals

    return `
      <input
        type="number"
        id="answer"
        value="${value}"
        placeholder="${placeholder}"
        ${min}
        ${max}
        ${step}
      >
    `;
  }

  private static renderDateInput(value: string): string {
    const today = new Date().toISOString().split('T')[0];
    const minDate = '1900-01-01';
    return `<input type="date" id="answer" value="${value}" min="${minDate}" max="${today}">`;
  }

  private static renderTextarea(value: string, placeholder: string): string {
    return `
      <textarea id="answer" placeholder="${placeholder}" style="overflow:hidden;resize:none;min-height:48px;">${value}</textarea>
      <script>
        (function() {
          var ta = document.getElementById('answer');
          if (!ta) return;
          function autoResize() {
            ta.style.height = 'auto';
            ta.style.height = ta.scrollHeight + 'px';
          }
          ta.addEventListener('input', autoResize);
          // Initial resize for pre-filled value
          autoResize();
        })();
      </script>
    `;
  }

  private static renderSelect(value: string, options: string[] | undefined): string {
    if (!Array.isArray(options) || options.length === 0) {
      return '<div class="question-error">No options available.</div>';
    }
    const optionTags = options.map(option => 
      `<option value="${option}" ${value === option ? 'selected' : ''}>${option}</option>`
    ).join('');
    
    return `
      <select id="answer">
        <option value="">Select an option...</option>
        ${optionTags}
      </select>
    `;
  }

  private static renderRadioButtons(value: string, options: string[] | undefined): string {
    if (!Array.isArray(options) || options.length === 0) {
      return '<div class="question-error">No options available.</div>';
    }
    return options.map(option => `
      <label class="radio-option">
        <input type="radio" name="answer" value="${option}" ${value === option ? 'checked' : ''}>
        ${option}
      </label>
    `).join('');
  }

  private static renderCheckboxes(value: string | string[], options: string[] | undefined): string {
    if (!Array.isArray(options) || options.length === 0) {
      return '<div class="question-error">No options available.</div>';
    }
    const selectedAnswers = Array.isArray(value) ? value : [];
    return options.map(option => `
      <label class="checkbox-option">
        <input type="checkbox" name="answer" value="${option}" ${selectedAnswers.includes(option) ? 'checked' : ''}>
        ${option}
      </label>
    `).join('');
  }

  private static renderRating(value: string, maxRating: number, minLabel?: string, maxLabel?: string): string {
    const rating = parseInt(value) || 0;
    const stars = Array.from({length: maxRating}, (_, i) => i + 1)
      .map(star => `<span class="star${star <= rating ? ' selected' : ''}" data-rating="${star}" style="margin-right: 4px;">★</span>`)
      .join('');
    // The label row is hidden by default, shown on hover/drag/select
    let labelsRow = '';
    if (minLabel || maxLabel) {
      labelsRow = `<div class="rating-label-row" style="display: none; justify-content: space-between; margin-top: 2px; font-size: 0.85em; color: #888;">
        <span class="rating-label min-label">${minLabel ? minLabel : ''}</span>
        <span class="rating-label max-label">${maxLabel ? maxLabel : ''}</span>
      </div>`;
    }
    // Add a unique id for the rating container for event binding
    const ratingId = `rating-${Math.random().toString(36).substring(2, 11)}`;
    return `
      <div class="rating-container" id="${ratingId}" style="display: flex; flex-direction: column; align-items: center; gap: 2px; user-select: none;">
        <div class="selected-rating-number" style="display: none; font-size: 1.1em; font-weight: 500; margin-bottom: 2px;"></div>
        <div class="stars" style="display: flex; cursor: pointer;">${stars}</div>
        ${labelsRow}
      </div>
      <input type="hidden" id="answer" value="${value}">
      <script>
        (function() {
          const container = document.getElementById('${ratingId}');
          if (!container) return;
          const stars = container.querySelectorAll('.star');
          const labelRow = container.querySelector('.rating-label-row');
          const input = document.getElementById('answer');
          const numberDisplay = container.querySelector('.selected-rating-number');
          let isDragging = false;
          let currentRating = ${rating};

          function setRating(rating, updateInput = false) {
            stars.forEach((star, idx) => {
              if (idx < rating) star.classList.add('selected');
              else star.classList.remove('selected');
            });
            if (updateInput && input) input.value = rating;
            if (labelRow) labelRow.style.display = rating > 0 ? 'flex' : 'none';
            if (numberDisplay) {
              if (rating > 0) {
                numberDisplay.textContent = rating + ' / ' + stars.length;
                numberDisplay.style.display = 'block';
              } else {
                numberDisplay.textContent = '';
                numberDisplay.style.display = 'none';
              }
            }
          }

          function showLabels() {
            if (labelRow) labelRow.style.display = 'flex';
          }
          function hideLabels() {
            if (labelRow && !input.value) labelRow.style.display = 'none';
          }

          stars.forEach((star, idx) => {
            star.addEventListener('mouseenter', () => {
              setRating(idx + 1, false);
              showLabels();
            });
            star.addEventListener('mouseleave', () => {
              setRating(parseInt(input.value) || 0, false);
              hideLabels();
            });
            star.addEventListener('mousedown', (e) => {
              isDragging = true;
              setRating(idx + 1, true);
              showLabels();
            });
            star.addEventListener('click', () => {
              setRating(idx + 1, true);
              showLabels();
            });
          });

          container.addEventListener('mouseleave', () => {
            isDragging = false;
            setRating(parseInt(input.value) || 0, false);
            hideLabels();
          });

          container.addEventListener('mousemove', (e) => {
            if (!isDragging) return;
            const rect = container.querySelector('.stars').getBoundingClientRect();
            const x = e.clientX - rect.left;
            const starWidth = rect.width / stars.length;
            let hoverRating = Math.ceil(x / starWidth);
            hoverRating = Math.max(1, Math.min(stars.length, hoverRating));
            setRating(hoverRating, true);
            showLabels();
          });

          document.addEventListener('mouseup', () => {
            isDragging = false;
          });

          // Touch support
          container.addEventListener('touchstart', (e) => {
            isDragging = true;
            const touch = e.touches[0];
            handleTouch(touch);
            showLabels();
          });
          container.addEventListener('touchmove', (e) => {
            if (!isDragging) return;
            const touch = e.touches[0];
            handleTouch(touch);
            showLabels();
          });
          container.addEventListener('touchend', () => {
            isDragging = false;
          });
          function handleTouch(touch) {
            const rect = container.querySelector('.stars').getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const starWidth = rect.width / stars.length;
            let hoverRating = Math.ceil(x / starWidth);
            hoverRating = Math.max(1, Math.min(stars.length, hoverRating));
            setRating(hoverRating, true);
          }

          // Show labels and number if already selected
          if (input.value && labelRow) labelRow.style.display = 'flex';
          if (input.value && numberDisplay) {
            numberDisplay.textContent = input.value + ' / ' + stars.length;
            numberDisplay.style.display = 'block';
          }
        })();
      </script>
    `;
  }

  private static renderTimeInput(value: string): string {
    return `<input type="time" id="answer" value="${value}">`;
  }

  private static renderStaticText(text: string, description?: string): string {
    return `
      <div class="static-text">
        <h2 style="white-space: pre-wrap;">${text}</h2>
        ${description ? `<p style="white-space: pre-wrap;">${description}</p>` : ''}
      </div>
      <input type="hidden" id="answer" value="static">
    `;
  }

  private static renderGrid(currentAnswer: string | string[], rows: string[], columns: string[]): string {
    if (!Array.isArray(rows) || !Array.isArray(columns) || rows.length === 0 || columns.length === 0) {
      return '<div class="question-error">Grid question requires both rows and columns.</div>';
    }

    let answers: Record<string, string> = {};
    if (currentAnswer && typeof currentAnswer === 'string') {
      try {
        answers = JSON.parse(currentAnswer);
      } catch {
        answers = {};
      }
    }

    const headerRow = columns.map(col => `<th class="grid-header">${col}</th>`).join('');
    const dataRows = rows.map((row, rowIndex) => {
      const cells = columns.map((col, colIndex) => {
        const inputName = `grid_${rowIndex}`;
        const inputId = `grid_${rowIndex}_${colIndex}`;
        const isChecked = answers[rowIndex.toString()] === col ? 'checked' : '';
        return `
          <td class="grid-cell">
            <label class="grid-radio-label">
              <input type="radio" name="${inputName}" value="${col}" id="${inputId}" ${isChecked}>
              <span class="grid-radio-text">${col}</span>
            </label>
          </td>
        `;
      }).join('');

      return `
        <tr class="grid-row">
          <td class="grid-statement">${row}</td>
          ${cells}
        </tr>
      `;
    }).join('');

    return `
      <div class="grid-question">
        <div class="grid-table-container">
          <table class="grid-table">
            <thead>
              <tr>
                <th class="grid-statement-header"></th>
                ${headerRow}
              </tr>
            </thead>
            <tbody>
              ${dataRows}
            </tbody>
          </table>
        </div>
        <input type="hidden" id="answer" value='${JSON.stringify(answers)}'>
      </div>
    `;
  }

  static initializeGrid(): void {
    const gridQuestion = document.querySelector('.grid-question');
    if (!gridQuestion) return;

    const hiddenInput = document.getElementById('answer') as HTMLInputElement;
    if (!hiddenInput) return;

    gridQuestion.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      if (target.type !== 'radio') return;

      const rowIndex = target.name.split('_')[1];
      const answers: Record<string, string> = hiddenInput.value ? JSON.parse(hiddenInput.value) : {};
      answers[rowIndex] = target.value;
      hiddenInput.value = JSON.stringify(answers);
    });
  }
}
